import PropTypes from "prop-types"
import React, { useContext, useEffect, useCallback } from "react"
import { useParams } from "react-router-dom"
import { Grid, Paper, TextField, Autocomplete } from "@mui/material"
import { useSnackbar } from "notistack"
import i18n from "i18n"

import ContactField from "components/Contacts/ContactField"
import ProjectContext from "context/projects/projectContext"
import { formatDateForInput } from "components/Invoices/utils/invoiceUtils"
import SerieSelector from "components/ProfileRefinament/series/SerieSelector"

const PurchaseOrderInfo = ({
  purchaseOrderData,
  setPurchaseOrderData,
  hasValidationErrors,
  onInvalidContact,
  readOnly = false,
  organizationId,
  isNew,
  selectedSerieSK,
  setSelectedSerieSK,
}) => {
  const {
    projects = [],
    getProjects,
    loadingProjects,
  } = useContext(ProjectContext)
  const { purchaseOrderId, projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const isProjectSelectorLocked =
    readOnly || (purchaseOrderId === "new" && !!projectId)

  const selectedProject =
    projects.find(p => p.projectId === purchaseOrderData.projectId) || null

  useEffect(() => {
    if (projects.length === 0) {
      getProjects()
    }
  }, [projects, getProjects])

  useEffect(() => {
    if (readOnly) return
    if (projectId && projects.length > 0) {
      const found = projects.find(p => p.projectId === projectId)
      if (found) {
        setPurchaseOrderData(prev => {
          if (prev.projectId === found.projectId && prev.contact) return prev
          const clientSK = prev.contact || found.client || ""
          return {
            ...prev,
            projectId: found.projectId,
            contact: clientSK,
          }
        })
      }
    }
  }, [projectId, projects, setPurchaseOrderData, readOnly])

  useEffect(() => {
    if (readOnly) return
    if (projectId && projects.length > 0 && !purchaseOrderData.projectId) {
      const found = projects.find(p => p.projectId === projectId)
      if (found) {
        const clientSK = found.client || ""
        setPurchaseOrderData(prev => ({
          ...prev,
          projectId: found.projectId,
          contact: clientSK || prev.contact,
        }))
      }
    }
  }, [
    projectId,
    projects,
    setPurchaseOrderData,
    purchaseOrderData.projectId,
    readOnly,
  ])

  const handleProjectChange = (event, newValue) => {
    if (isProjectSelectorLocked) return
    const clientSK = newValue && newValue.client ? newValue.client : ""
    setPurchaseOrderData(prev => ({
      ...prev,
      projectId: newValue ? newValue.projectId : null,
      contact: clientSK,
    }))
  }

  const handleFieldChange = field => event => {
    if (readOnly) return
    setPurchaseOrderData(prev => ({
      ...prev,
      [field]: event.target.value,
    }))
  }

  const handleIssueDateChange = event => {
    if (readOnly) return
    const newIssueDate = event.target.value
    setPurchaseOrderData(prev => {
      const currentDue = formatDateForInput(prev.dueDate)
      const shouldAdjustDue =
        newIssueDate && currentDue && currentDue < newIssueDate
      return {
        ...prev,
        issueDate: newIssueDate,
        ...(shouldAdjustDue ? { dueDate: newIssueDate } : {}),
      }
    })
  }

  const issueDateValue = formatDateForInput(purchaseOrderData.issueDate)
  const readOnlyStyle = isProjectSelectorLocked
    ? { pointerEvents: "none", background: "#f5f5f5" }
    : {}

  const handleInvalidContact = useCallback(() => {
    enqueueSnackbar(i18n.t("invoices.selectClient"), { variant: "error" })
    onInvalidContact(true)
  }, [enqueueSnackbar, onInvalidContact])

  const handleContactChange = (_e, newValue) => {
    if (readOnly) return
    const contactSK = newValue ? newValue.SK : ""
    onInvalidContact(false)
    setPurchaseOrderData(prev => ({
      ...prev,
      contact: contactSK,
    }))
  }

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 2 }}>
      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={3}>
          <Autocomplete
            options={projects}
            getOptionLabel={option => option.name || ""}
            value={selectedProject}
            onChange={isProjectSelectorLocked ? undefined : handleProjectChange}
            loading={loadingProjects}
            noOptionsText={
              loadingProjects
                ? i18n.t("invoices.loadingProjects")
                : i18n.t("invoices.noProjects")
            }
            open={isProjectSelectorLocked ? false : undefined}
            disableClearable={isProjectSelectorLocked ? true : undefined}
            forcePopupIcon={isProjectSelectorLocked ? false : undefined}
            readOnly={isProjectSelectorLocked}
            slotProps={{
              textField: {
                inputProps: {
                  tabIndex: isProjectSelectorLocked ? -1 : 0,
                  style: readOnlyStyle,
                },
              },
            }}
            renderInput={params => (
              <TextField
                {...params}
                label={i18n.t("invoices.project")}
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  readOnly: isProjectSelectorLocked,
                  style: readOnlyStyle,
                }}
              />
            )}
            sx={{ mb: 2 }}
          />
        </Grid>
        <Grid item xs={3}>
          <ContactField
            label={i18n.t("costs.columns.provider")}
            value={purchaseOrderData.contact || ""}
            onChange={handleContactChange}
            readOnly={readOnly}
            required
            error={
              !readOnly &&
              hasValidationErrors &&
              (!purchaseOrderData.contact ||
                purchaseOrderData.contact.trim() === "")
            }
            onInvalidContact={readOnly ? undefined : handleInvalidContact}
          />
        </Grid>
        <Grid item xs={2}>
          <SerieSelector
            organizationId={organizationId}
            serieType="purchase_orders"
            mode={isNew ? "create" : "view"}
            value={selectedSerieSK}
            onChange={setSelectedSerieSK}
            currentNumber={purchaseOrderData.purchaseOrderNumber}
            label={i18n.t("purchaseOrders.purchaseOrderNumber")}
            hasError={hasValidationErrors}
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            label={i18n.t("invoices.issueDate")}
            variant="outlined"
            fullWidth
            type="date"
            required
            disabled={readOnly}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
            value={issueDateValue}
            onChange={readOnly ? undefined : handleIssueDateChange}
            error={
              hasValidationErrors &&
              (!purchaseOrderData.issueDate || issueDateValue.trim() === "")
            }
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            label={i18n.t("invoices.dueDate")}
            variant="outlined"
            fullWidth
            type="date"
            disabled={readOnly}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
            value={formatDateForInput(purchaseOrderData.dueDate)}
            onChange={readOnly ? undefined : handleFieldChange("dueDate")}
            inputProps={{
              min: issueDateValue || undefined,
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  )
}

PurchaseOrderInfo.propTypes = {
  purchaseOrderData: PropTypes.shape({
    projectId: PropTypes.string,
    purchaseOrderNumber: PropTypes.string,
    contact: PropTypes.string,
    issueDate: PropTypes.string,
    dueDate: PropTypes.string,
  }).isRequired,
  setPurchaseOrderData: PropTypes.func.isRequired,
  hasValidationErrors: PropTypes.bool,
  onInvalidContact: PropTypes.func,
  readOnly: PropTypes.bool,
  organizationId: PropTypes.string.isRequired,
  isNew: PropTypes.bool.isRequired,
  selectedSerieSK: PropTypes.string,
  setSelectedSerieSK: PropTypes.func,
}

export default PurchaseOrderInfo
